import { AI_PLANNER_BRIDGE_INSTANCE_PARAM } from '@atlassian/ai-planner-embed-bridge/AiPlannerBridgeCommands';

/**
 * Tenant context Planner needs to boot quickly in embed mode.
 *
 * Per the planner's embed-mode contract, these are passed as hints for
 * telemetry / feature-gate targeting and to skip a tenant lookup — they are
 * never used for authorization (all planner data calls remain auth-scoped).
 */
export type AiPlannerEmbedContext = {
	cloudId: string;
	/** Null for anonymous users / before tenant context initializes. */
	accountId: string | null;
};

/**
 * Jira's current color mode, as read from Jira's own theme state (e.g.
 * `useThemeObserver` / the `data-color-mode` attribute) — NEVER the OS/browser
 * `prefers-color-scheme` preference. Jira and the OS can disagree (e.g. a user
 * pins Jira to dark while their OS is light), so the planner must match Jira's
 * chrome, not the device.
 */
export type AiPlannerColorMode = 'light' | 'dark';

type PlannerSearchParamsInput = string | URLSearchParams;

const HOST_OWNED_PLANNER_PARAMS = [
	'embed',
	'cloudId',
	'accountId',
	'colorMode',
	'bifrost-override',
	'ai-planner-bifrost-override',
	AI_PLANNER_BRIDGE_INSTANCE_PARAM,
];

/**
 * Builds the Planner embed URL that the iframe modal renders.
 *
 * The path mirrors the AI planning link convention:
 * - a specific plan resolves to `<base>/plan/:planId`
 * - the planner overview / create flow resolves to `<base>`
 *
 * Embed mode is requested via query params (the planner's documented contract):
 * - `embed=true` — renders the planner without its top nav / global sidebar
 * - `cloudId` — lets the planner skip the `/_edge/tenant_info` round-trip
 * - `accountId` — seeds the planner's telemetry / feature-gate targeting
 * - `colorMode` — tells the planner whether to render light or dark, matching
 *   Jira's current theme (never the OS preference)
 *
 * Keeping this in one place means the iframe modal and any consumer wiring
 * stay in sync with the planner embed contract.
 *
 * IMPORTANT: the planner reads `colorMode` (like the rest of embed context)
 * once per iframe load — it never re-reads it live. If Jira's theme changes
 * while an iframe built from this URL is still mounted, the caller must
 * recreate/reload that iframe (e.g. bump a `key`, or re-set `src`) for the new
 * value to take effect.
 */
// The planner is served from the same origin as Jira, so the iframe uses the
// relative `/planner` base. A specific planner build can still be pinned at
// runtime via the `ai-planner-bifrost-override` query param (see below).
const PLANNER_BASE_PATH = '/planner';

/**
 * The planner's origin, when it is served from a different origin than Jira.
 * Returns `null` when the planner is same-origin (the relative `/planner` base),
 * in which case preconnecting is unnecessary. Used to warm the connection
 * (preconnect) before the iframe mounts.
 */
export const getAiPlannerOrigin = (): string | null => {
	try {
		// An absolute base has an origin distinct from a relative path.
		const url = new URL(PLANNER_BASE_PATH, 'https://relative.invalid');
		return url.origin === 'https://relative.invalid' ? null : url.origin;
	} catch {
		return null;
	}
};

/**
 * Reads the `ai-planner-bifrost-override` query param from the current Jira page
 * URL and forwards it to the planner iframe so a specific planner Bifrost build
 * can be pinned via the URL (e.g. for QA, dogfooding, or pre-release validation).
 *
 * Returns `null` when the param is absent or when running outside the browser
 * (SSR), in which case no override is appended and the planner serves its
 * default build.
 */
export const getBifrostOverrideFromLocation = (): string | null => {
	if (typeof globalThis.location === 'undefined') {
		return null;
	}
	const value = new URLSearchParams(globalThis.location.search).get('ai-planner-bifrost-override');
	return value != null && value !== '' ? value : null;
};

export const getAiPlannerUrl = (
	embedContext: AiPlannerEmbedContext,
	planId?: string,
	// When set, forwarded to the planner as `bifrost-override` so a specific
	// planner Bifrost build can be pinned (sourced from the Jira page URL's
	// `ai-planner-bifrost-override` query param via getBifrostOverrideFromLocation).
	bifrostOverride?: string,
	plannerSearch?: PlannerSearchParamsInput,
	// Per-iframe bridge instance id. When set, forwarded so the planner derives a
	// private Rovo bridge channel for this embed; when absent the planner falls
	// back to the global bridge ids.
	bridgeInstanceId?: string,
	// Jira's current color mode, sourced from Jira's theme state (see
	// `AiPlannerColorMode`) — never the OS preference. Defaults to 'light' for
	// callers that haven't resolved a theme yet (e.g. very first paint).
	colorMode: AiPlannerColorMode = 'light',
): string => {
	const path =
		planId != null && planId !== ''
			? `${PLANNER_BASE_PATH}/plan/${encodeURIComponent(planId)}`
			: PLANNER_BASE_PATH;

	const params = new URLSearchParams(plannerSearch);
	for (const param of HOST_OWNED_PLANNER_PARAMS) {
		params.delete(param);
	}

	params.set('embed', 'true');
	params.set('cloudId', embedContext.cloudId);
	if (embedContext.accountId != null && embedContext.accountId !== '') {
		params.set('accountId', embedContext.accountId);
	}
	params.set('colorMode', colorMode);
	if (bifrostOverride != null && bifrostOverride !== '') {
		params.set('bifrost-override', bifrostOverride);
	}
	if (bridgeInstanceId != null && bridgeInstanceId !== '') {
		params.set(AI_PLANNER_BRIDGE_INSTANCE_PARAM, bridgeInstanceId);
	}

	return `${path}?${params.toString()}`;
};
